import { useWorkpaperContext } from '../../providers/WorkpaperProvider';
import Xero from '../../assets/xero.png';
import RelativeTimestamp from '../../components/RelativeTimestamp';
import { BadgeV3, ButtonV2, Dialog } from '@cimplico/components';
import { ArrowsRightLeftIcon, CheckCircleIcon } from '@heroicons/react/20/solid';
import { WorkpaperSourceEnum } from '../../types/workpaper';
import { useUpdateWorkpapers } from '../../queries/use-update-workpapers';
import { useState } from 'react';
import { toastNotification } from '../../components/toast/Toast';

type TrialBalanceProviderProps = {
    hasItems: boolean;
    lastSynced?: string;
    onXeroError: (params: { name: string; message: string }) => void;
};

function TrialBalanceProvider({ hasItems, lastSynced, onXeroError }: TrialBalanceProviderProps): JSX.Element {
    const { id, provider, entity } = useWorkpaperContext();
    const [confirm, setConfirm] = useState(false);

    const { mutate, isLoading } = useUpdateWorkpapers();

    function switchToXero(): void {
        setConfirm(false);
        mutate([{ id, fields: { provider: 'XERO' } }], {
            onError: (res) => {
                if (res.response?.data?.results[0]?.error.fields?.xeroErrorName) {
                    const msg = res.response?.data?.results[0]?.error?.fields;
                    console.log(msg);
                    onXeroError({ name: msg.xeroErrorName, message: msg.refresh });
                    return;
                }
                toastNotification('error', 'Unable to change provider', res.response?.data?.results[0]?.error?.fields?.errorMessage);
            },
        });
    }

    if (!entity) {
        return <></>;
    }

    return (
        <>
            <Dialog
                title="Switch to Xero"
                description="Do you want to update this workpaper to use Xero as its trial balance source? This cannot be reversed."
                onClose={(): void => setConfirm(false)}
                isOpen={confirm}
                primaryAction="Confirm"
                secondaryAction="Cancel"
                onSecondaryActionClick={(): void => setConfirm(false)}
                onPrimaryActionClick={switchToXero}
                type="info"
            />
            <div className="flex items-center gap-2 mb-4">
                {provider === WorkpaperSourceEnum.XERO && <img src={Xero} className="h-6 w-6" />}
                {lastSynced && <RelativeTimestamp date={lastSynced} className="text-sm text-gray-600" prefix=" Last sync:" />}
                {provider === WorkpaperSourceEnum.XERO && <BadgeV3 label="In use" color="green" leftIcon={<CheckCircleIcon />} />}
                {provider !== WorkpaperSourceEnum.XERO && entity.integrations.xero && (
                    <ButtonV2
                        label={isLoading ? 'Switching...' : 'Switch to Xero'}
                        size="xs"
                        type="secondary"
                        leftIcon={<ArrowsRightLeftIcon />}
                        onClick={(): void => setConfirm(true)}
                        isDisabled={isLoading || hasItems}
                        isLoading={isLoading}
                        disabledHint={hasItems ? 'Only workpapers with an empty trial balance can update its provider.' : undefined}
                    />
                )}
            </div>
        </>
    );
}

export default TrialBalanceProvider;
